import { ref, computed } from 'vue';
import { startTaskApi } from '@Api/dashboard.api';
import { useDashboardStore } from '@Stores/dashboard.store';
import type { TaskItem } from '@Types/dashboard.types';
import {
    hasPrimingConfig,
    resolvePrimingConfig,
    type PrimingConfig,
} from '@Components/priming/primingConfigs';

// Module-level singletons: dashboard mounts the sheet once and multiple
// consumers (TaskCard via useTaskNavigation) share this state.
const isOpen = ref(false);
const pendingTask = ref<TaskItem | null>(null);

export function useTaskPriming() {
    const activeConfig = computed<PrimingConfig | null>(() =>
        pendingTask.value ? resolvePrimingConfig(pendingTask.value) : null,
    );

    const taskHasPriming = (task: TaskItem): boolean => hasPrimingConfig(task);

    const openSheet = (task: TaskItem) => {
        pendingTask.value = task;
        isOpen.value = true;

        // Flip pending → in_progress as soon as the user engages, so the
        // dashboard reflects the new state on reload.
        if (task.status === 'pending') {
            startTaskApi(task.id)
                .then(() => {
                    useDashboardStore().loadDashboard();
                })
                .catch(() => {
                    // Non-blocking.
                });
        }
    };

    const closeSheet = () => {
        isOpen.value = false;
        pendingTask.value = null;
    };

    return {
        isOpen,
        pendingTask,
        activeConfig,
        taskHasPriming,
        openSheet,
        closeSheet,
    };
}
